/* eslint-disable @next/next/no-img-element */
'use client';

import { atId } from '@ab-inbev-eu/ui-library/components/utils/index';
import Link from 'next/link';
import Button from '@ab-inbev-eu/ui-library/components/basic/Button';
import BlurredBg from '@ab-inbev-eu/ui-library/components/basic/BlurredBg';
import { redirect } from 'next/navigation';

import type { getCampaignByQrHash } from '../../../../../utils/api/campaign';
import { getCampaignStatus } from '../../../../../utils/functions.shared';
import { AgeGate } from '../[...route]/_components/age-gate/AgeGate';
import { useTranslation } from '../../../../../i18n/client';
import amendSessionStore from '../../../../../utils/amendSessionStore';
import { useAnalytics } from '../../../../../components/analytics/hooks';
import { trackCampaignEvent } from '../../../../../utils/braze';

import { FooterLogoWrapper } from './FooterLogoWrapper';

export default function FallbackPage({
  qrId,
  campaign,
}: {
  qrId: string;
  campaign: Awaited<ReturnType<typeof getCampaignByQrHash>>;
}) {
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const { fallback } = campaign.template;

  const campaignStatus = getCampaignStatus(campaign);
  let headerKey: string | null = null;
  let headerValue: string | null = null;
  let descriptionKey: string | null = null;
  let descriptionValue: string | null = null;
  if (campaignStatus === 'notStarted') {
    headerKey = fallback.headerCampaignNotStarted[0] ?? 'fallback.header';
    headerValue = fallback.headerCampaignNotStarted[1] ?? 'Thanks for scanning!';
    descriptionKey = fallback.descriptionCampaignNotStarted[0] ?? 'fallback.description.notStarted';
    descriptionValue = fallback.descriptionCampaignNotStarted[1] ?? 'This promotion is not yet live. Check back soon!';
  } else if (campaignStatus === 'ended') {
    headerKey = fallback.headerCampaignEnded[0] ?? 'fallback.header';
    headerValue = fallback.headerCampaignEnded[1] ?? 'Thanks for scanning!';
    descriptionKey = fallback.descriptionCampaignEnded[0] ?? 'fallback.description.ended';
    descriptionValue = fallback.descriptionCampaignEnded[1] ?? 'This promotion has now finished.';
  } else {
    redirect(`/qr/${qrId}`);
  }

  const brandLogoUrl =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    campaign?.branding.assets && 'brandLogoUrl' in campaign.branding.assets ?
      (campaign.branding.assets.brandLogoUrl as string)
    : '';

  async function onPassed() {
    await amendSessionStore((session) => {
      return {
        ...session,
        globalResolvedConditions: [...session.globalResolvedConditions, 'age-passed'],
      };
    });
  }

  return (
    <AgeGate
      alwaysVisible={false}
      onPassed={() => void onPassed()}
      ageGateConfig={campaign.template.ageGate}
      brandLogoUrl={brandLogoUrl}
    >
      <BlurredBg>
        <FooterLogoWrapper>
          <div className="flex size-full flex-col items-center justify-center p-10 text-center text-neutral-100">
            <img src={brandLogoUrl} className="!static mb-10 !h-[100px]" alt="brand-logo" />
            <h1 className="font-headline-m text-headline-m pb-3" data-test-id={atId('fallbackScreenTitle', 'Val')}>
              {t(headerKey, headerValue)}
            </h1>
            <p
              className="font-body-m text-body-m whitespace-pre-wrap"
              data-test-id={atId('fallbackScreenDescription', 'Val')}
            >
              {t(descriptionKey, descriptionValue)}
            </p>
            {fallback.link && (
              <Link
                className="mt-10 w-full"
                href={t(fallback.link.path[0], fallback.link.path[1])}
                target={fallback.link.path[1]?.startsWith('.') ? '_self' : '_blank'}
              >
                <Button
                  buttonProps={{
                    className: 'w-full font-subtitle-m [&]:text-subtitle-m text-on-primary',
                    ['data-test-id' as 'datatype']: atId('fallbackScreenLink', 'Btn'),
                  }}
                >
                  {t(fallback.link.label[0], fallback.link.label[1])}
                </Button>
              </Link>
            )}
            {fallback.primaryBtn && (
              <Button
                buttonProps={{
                  onClick: () => {
                    analytics.track('Notify Me Clicked');
                    trackCampaignEvent({
                      name: 'remind_me',
                      data: {
                        fallback_type: campaignStatus,
                        campaign_name: campaign.name,
                        brand: campaign.brands,
                      },
                    });
                  },
                  className: 'w-full mt-10 font-subtitle-m [&]:text-subtitle-m text-on-primary',
                  ['data-test-id' as 'datatype']: atId('fallbackScreenLink', 'Btn'),
                }}
              >
                {fallback.primaryBtn.label && t(fallback.primaryBtn.label[0], fallback.primaryBtn.label[1])}
              </Button>
            )}
          </div>
        </FooterLogoWrapper>
      </BlurredBg>
    </AgeGate>
  );
}
